import {Place} from '@wix/ambassador-seating-v1-seating-plan/types'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {BI_ORIGINS, PlaceWithTicketInfo} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {isPlaceReservableAsWhole} from '../../../selectors/seating/place'
import {isDonationValid} from '../../../services/donations'
import {DONATION_ERROR} from '../../../types'
import {FormEntry} from '../../chunks'
import {Modal} from '../modal'
import {Desktop} from './desktop'
import {ErrorModal} from './error-modal'
import {SeatingPlanProps} from './interfaces'
import {LandscapeModal} from './landscape-modal'
import {Loader} from './loader'
import {Mobile} from './mobile'
import {PricingOptionModal} from './pricing-options-modals/pricing-option-modal'
import {TablePricingOptionsModal} from './pricing-options-modals/table-pricing-options-modal'
import {classes} from './seating-plan.st.css'

export const SeatingPlan = ({
  setPlaceQuantity,
  seatingCheckout,
  loading,
  seatingError,
  reservationError,
  placesInBasket,
  showAccessibilityMode,
  chooseSeatMode,
  chooseMultipleSeatsMode,
  setShowAccessibilityMode,
  selectedPlace,
  unselectPlace,
  showPriceOptionsPlace,
  setShowPlaceDescription,
  clickPlace,
  firstPlaceIdWithError,
}: SeatingPlanProps) => {
  const {t} = useTranslation()
  const {isMobile: mobile} = useEnvironment()
  const desktop = !mobile
  const focusErrorRequest = useRef<boolean>(false)

  useEffect(() => {
    if (chooseSeatMode || chooseMultipleSeatsMode) {
      window.parent.postMessage({seatingPlanLoaded: true}, '*')
    }
  }, [])

  useEffect(() => {
    if (chooseSeatMode) {
      const valid = placesInBasket?.[0]
        ? isDonationValid(placesInBasket[0].ticket, placesInBasket[0].donation, {
            excludeErrors: [DONATION_ERROR.MINIMUM_NOT_REACHED],
          })
        : true

      window.parent.postMessage({placesInBasket, valid}, '*')
    } else if (chooseMultipleSeatsMode) {
      const invalidDonation = placesInBasket?.find(
        place => !isDonationValid(place.ticket, place.donation, {excludeErrors: [DONATION_ERROR.MINIMUM_NOT_REACHED]}),
      )

      window.parent.postMessage({placesInBasket, valid: !invalidDonation}, '*')
    }

    document.querySelector('#PAGES_CONTAINER')?.classList.add('ignore-focus')

    return () => document.querySelector('#PAGES_CONTAINER')?.classList.remove('ignore-focus')
  }, [placesInBasket])

  useEffect(() => {
    if (focusErrorRequest.current) {
      focusErrorRequest.current = false

      if (firstPlaceIdWithError) {
        focusElement({
          selector: `${hookToAttributeSelector(`ticket-${firstPlaceIdWithError}`)} ${hookToAttributeSelector(
            DH.DONATION_INPUT,
          )}`,
        })
      }
    }
  })

  const handleCheckout = useCallback(() => {
    FormEntry.preload()
    focusErrorRequest.current = true
    seatingCheckout()
  }, [seatingCheckout])

  const onRemoveClick = useCallback((place: PlaceWithTicketInfo) => {
    setPlaceQuantity({
      placeId: place.id,
      count: 0,
      origin: BI_ORIGINS.SEATINGS_MODAL_BASKET,
    })
  }, [])

  const mobileTabs = useMemo(
    () => [{title: t('seatingMapModal_mapTab_title')}, {title: t('seatingMapModal_listTab_title')}],
    [t],
  )

  const onTabClick = useCallback(() => setShowAccessibilityMode(!showAccessibilityMode), [showAccessibilityMode])

  const onCloseClick = useCallback(() => {
    if (selectedPlace?.id) {
      unselectPlace()
    }
  }, [selectedPlace])

  const onPlaceClick = useCallback(
    ({place}: {place?: Place}) => {
      clickPlace({place, origin: BI_ORIGINS.MAP})
    },
    [clickPlace],
  )

  return (
    <Modal
      className={classNames({
        [classes.desktopModal]: desktop && !chooseSeatMode && !chooseMultipleSeatsMode,
        [classes.hideCloseButton]: chooseSeatMode || chooseMultipleSeatsMode,
      })}
      closeButtonAsButton={desktop}
      dataHook={DH.SEATINGS_MODAL}
      title={mobile ? t('seatingMapModal_title') : undefined}
      withoutPadding={desktop}
      tabs={mobile ? mobileTabs : undefined}
      activeTabIndex={showAccessibilityMode ? 1 : 0}
      onTabClick={onTabClick}
    >
      {mobile && <LandscapeModal />}
      <ErrorModal seatingError={seatingError} reservationError={reservationError} />
      {showPriceOptionsPlace ? (
        isPlaceReservableAsWhole(showPriceOptionsPlace) ? (
          <TablePricingOptionsModal />
        ) : (
          <PricingOptionModal />
        )
      ) : null}
      {loading ? <Loader /> : null}
      {mobile ? (
        <Mobile
          onRemoveClick={onRemoveClick}
          onCheckoutClick={handleCheckout}
          onPlaceClick={onPlaceClick}
          onCloseClick={onCloseClick}
          selectSeatForDescription={setShowPlaceDescription}
        />
      ) : (
        <Desktop
          onPlaceClick={onPlaceClick}
          onRemoveClick={onRemoveClick}
          onCheckoutClick={handleCheckout}
          showAccessibilityMode={showAccessibilityMode}
          selectSeatForDescription={setShowPlaceDescription}
        />
      )}
    </Modal>
  )
}
